export interface Highlight {
  questionId: number;
  text: string;
  startOffset: number;
  endOffset: number;
  color: string;
}

export const HIGHLIGHT_STORAGE_KEY = "exam_highlights";

export const getHighlights = (): Record<string, Highlight[]> => {
  const stored = localStorage.getItem(HIGHLIGHT_STORAGE_KEY);

  return stored ? JSON.parse(stored) : {};
};

export const saveHighlights = (highlights: Record<string, Highlight[]>) => {
  localStorage.setItem(HIGHLIGHT_STORAGE_KEY, JSON.stringify(highlights));
};

export const addHighlight = (
  sectionId: number,
  questionId: number,
  text: string,
  startOffset: number,
  endOffset: number,
  color = "yellow"
) => {
  const highlights = getHighlights();

  const questionHighlights = highlights[`${sectionId}-${questionId}`] || [];

  questionHighlights.push({
    questionId,
    text,
    startOffset,
    endOffset,
    color,
  });

  const sortedHighlights = [...questionHighlights].sort(
    (a, b) => b.startOffset - a.startOffset
  );

  highlights[`${sectionId}-${questionId}`] = sortedHighlights;

  saveHighlights(highlights);
};

export const removeHighlight = (
  sectionId: number,
  questionId: number,
  index: number
) => {
  const highlights = getHighlights();

  if (highlights[`${sectionId}-${questionId}`]) {
    highlights[`${sectionId}-${questionId}`].splice(index, 1);
    saveHighlights(highlights);
  }
};

export const applyHighlights = (
  element: HTMLElement,
  sectionId: number,
  questionId: number
) => {
  const highlights = getHighlights();
  const questionHighlights = highlights[`${sectionId}-${questionId}`] || [];

  const existingHighlights = element.querySelectorAll("span[data-highlight]");

  existingHighlights.forEach((el) => {
    const parent = el.parentNode;

    if (parent) {
      parent.replaceChild(document.createTextNode(el.textContent || ""), el);
    }
  });

  element.normalize();

  questionHighlights.forEach((highlight, index) => {
    const textNode = element.firstChild;

    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

    const text = textNode.textContent || "";

    if (
      highlight.startOffset > text.length ||
      highlight.endOffset > text.length
    )
      return;

    try {
      const range = document.createRange();

      range.setStart(textNode, highlight.startOffset);
      range.setEnd(textNode, highlight.endOffset);

      const span = document.createElement("span");

      span.style.backgroundColor = highlight.color;
      span.setAttribute("data-highlight", index.toString());
      range.surroundContents(span);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to apply highlight:", error);
    }
  });
};

export const getHighlightMenuItems = (
  onHighlight: () => void,
  onRemoveHighlight?: () => void
) => {
  if (!onRemoveHighlight) {
    return [
      {
        key: "highlight",
        label: "Highlight",
        onClick: onHighlight,
      },
    ];
  }

  if (onRemoveHighlight) {
    return [
      {
        key: "remove-highlight",
        label: "Remove Highlight",
        onClick: onRemoveHighlight,
      },
    ];
  }

  return [
    {
      key: "highlight",
      label: "Highlight",
      onClick: onHighlight,
    },
  ];
};

export const removeOldHighLight = (element: HTMLElement) => {
  const existingHighlights = element.querySelectorAll("span[data-highlight]");

  if (!existingHighlights.length) return;

  existingHighlights.forEach((el) => {
    const parent = el.parentNode;

    if (!parent) return;

    parent.replaceChild(document.createTextNode(el.textContent || ""), el);
    parent.normalize();
  });

  element.normalize();
};
